/// <amd-module name="VSS/Features/ReduxDirectory/Redux/DirectorySaga" />
import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { DirectoryGroupKeys } from "VSS/Features/Directory/DirectoryConstants";
import { ExceptionInfo, IDirectoryArtifact } from "VSS/Features/Directory/DirectoryContracts";
import { DirectoryActions, DirectoryActionTypes } from "VSS/Features/ReduxDirectory/Redux/DirectoryActions";
import { getGroupCollapsedMap, isAllSectionLoading, isAllSectionPopulated } from "VSS/Features/ReduxDirectory/Redux/DirectorySelectors";
import { IVssPageContext } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { traceError } from "VSS/Platform/Trace";
import { HubViewStateActionTypes } from "VSS/Redux/Deprecated/HubViewState";
import { DirectoryConstants } from "../Constants";
import { IDirectoryContext, IDirectoryDataProviderData, IUnifiedDirectoryDataProviderData } from "../Contracts";

/**
 * Root level saga for all directory pages
 */
export function* directorySaga(): SagaIterator {
    // Register sagas to load all data in certain scenarios
    yield effects.takeEvery(DirectoryActionTypes.RowGroupToggled, groupToggled);
    yield effects.takeEvery(HubViewStateActionTypes.FilterStateChanged, requestAllData);

    // Register saga to FPS nav to urls
    yield effects.takeEvery(DirectoryActionTypes.NavigateToUrl, navigateToUrl);

    // receive the initial data
    yield effects.takeEvery(DirectoryActionTypes.ReceiveInitialData, receiveInitialDataFromDataProvider);
    // receive the all data
    yield effects.takeEvery(DirectoryActionTypes.ReceiveAllData, receiveAllDataFromDataProvider);

    // Load the group mapping
    yield effects.call(loadSavedRowGroupCollapsedMapping);
}

/**
 * Saga which loads the initial data from the data provider
 */
export function* receiveInitialDataFromDataProvider(action: ActionsOfType<DirectoryActions, DirectoryActionTypes.ReceiveInitialData>): SagaIterator {
    const directoryData: IUnifiedDirectoryDataProviderData<IDirectoryArtifact> = action.payload;
    const error = _verifyAndReportValidData(directoryData);
    if (!error) {
        yield effects.put(DirectoryActions.initialDataLoaded(directoryData));

        // If there were no artifacts, expand the all group and load the items initially
        if (
            (!directoryData.customGroupings || Object.keys(directoryData.customGroupings).length === 0) &&
            (!directoryData.favorites || directoryData.favorites.length === 0)
        ) {
            yield effects.put(DirectoryActions.rowGroupToggled(DirectoryGroupKeys.AllGroupKey, false));
        }
    } else {
        const { message, exceptionInfo } = error;
        yield effects.put(DirectoryActions.initialDataLoadFailed(exceptionInfo));
        const directoryContext: IDirectoryContext = (yield effects.getContext("directoryContext")) as any;
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        yield effects.call(traceError, pageContext, directoryContext.hubName, "Directory", message);
    }
}

export function* loadSavedRowGroupCollapsedMapping(): SagaIterator {
    try {
        const directoryContext: IDirectoryContext = (yield effects.getContext("directoryContext")) as any;
        const key = getLocalStorageKey(directoryContext.hubName);
        const cachedStateJson = (yield effects.call([localStorage, localStorage.getItem], key)) as any;
        if (cachedStateJson) {
            const cachedState: { [key: string]: boolean } = JSON.parse(cachedStateJson);
            yield effects.put(DirectoryActions.rowGroupCollapsedInitialized(cachedState));
        }
    } catch {
        // no-op on error, use the default state
    }
}

export function* requestAllData(): SagaIterator {
    const allSectionLoading = (yield effects.select(isAllSectionLoading)) as any;
    const allSectionPopulated = (yield effects.select(isAllSectionPopulated)) as any;

    if (!allSectionLoading && !allSectionPopulated) {
        yield effects.put(DirectoryActions.beginLoadAllData());
        yield effects.put(DirectoryActions.requestAllData());
    }
}

export function* groupToggled(action: ActionsOfType<DirectoryActions, DirectoryActionTypes.RowGroupToggled>): SagaIterator {
    const { rowGroupKey, collapsed } = action.payload;
    if (rowGroupKey === DirectoryGroupKeys.AllGroupKey && !collapsed) {
        yield effects.call(requestAllData);
    }

    try {
        const directoryContext: IDirectoryContext = (yield effects.getContext("directoryContext")) as any;

        // Save the group state to local storage
        const groupState = (yield effects.select(getGroupCollapsedMap)) as any;
        const serializedGroupState = JSON.stringify(groupState);
        const key = getLocalStorageKey(directoryContext.hubName);
        yield effects.call([localStorage, localStorage.setItem], key, serializedGroupState);
    } catch {
        // no -op
    }
}

/**
 * Saga which loads the all data from the data provider
 * If the group toggled is the ALL group, and the all group has never been expanded, this saga will execute
 * @param action The group toggled action
 */
export function* receiveAllDataFromDataProvider(action: ActionsOfType<DirectoryActions, DirectoryActionTypes.ReceiveInitialData>): SagaIterator {
    const directoryContext: IDirectoryContext = (yield effects.getContext("directoryContext")) as any;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const directoryData: IDirectoryDataProviderData<IDirectoryArtifact> = action.payload;

    const error = _verifyAndReportValidData(directoryData);
    if (!error) {
        yield effects.put(DirectoryActions.loadAllDataSucceeded(directoryData));
    } else {
        const { message, exceptionInfo } = error;
        yield effects.put(DirectoryActions.loadAllDataFailed(exceptionInfo));

        yield effects.call(traceError, pageContext, directoryContext.hubName, "Directory", message);
    }
}

/**
 * Function which verifies payload from the data provider is well formed
 * @param pageData The data provider data
 * @returns An error payload if the data is invalid, undefined if the data is valid
 */
function _verifyAndReportValidData<T extends IDirectoryArtifact>(
    pageData: IDirectoryDataProviderData<T>
): { message: string; exceptionInfo: ExceptionInfo } | undefined {
    if (!pageData) {
        return { message: "The data provider didn't return any data", exceptionInfo: { exceptionMessage: "Server returned no response" } };
    } else if (pageData.exceptionInfo) {
        return {
            message: `The data provider contained exception info: ${JSON.stringify(pageData.exceptionInfo)}`,
            exceptionInfo: pageData.exceptionInfo
        };
    } else if (!pageData.artifacts) {
        return {
            message: "The data provider returned invalid data.",
            exceptionInfo: { exceptionMessage: "The server returned an invalid response" }
        };
    }
}

export function* navigateToUrl(action: ActionsOfType<DirectoryActions, DirectoryActionTypes.NavigateToUrl>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    yield effects.call(FastPageSwitch, pageContext, action.payload, /* recordHistoryEntry: */ true);
}

function getLocalStorageKey(areaName: string): string {
    return `${areaName}_${DirectoryConstants.LOCAL_STORAGE_KEY_PREFIX}`;
}
